<app-component-level-error-message [errorMessage]="errorMessage"></app-component-level-error-message>
@if (uiState === uiStateEnumForTemplate.ShowLoading) {
  <app-loading-card-content></app-loading-card-content>
} @else if (uiState === uiStateEnumForTemplate.ShowData && standardGameReport) {
  <div>
    <div class="justify-space-between flex-row align-start">
      <div>
        <div class="section-header">Standard Game Report</div>
        <div class="ticket-type-header">Draw Date: {{ standardGameReport.EndDate | date: 'MMM d, y HH:mm' }}</div>
      </div>
      <div class="report-header-actions">
        <div class="help-controls">
          <button mat-icon-button
                  class="glossary-button"
                  matTooltip="View field glossary"
                  (click)="openGlossary()">
            <mat-icon>menu_book</mat-icon>
          </button>
          <span class="help-toggle-label">Show Help</span>
          <mat-slide-toggle
            [checked]="showHelp()"
            (change)="showHelp.set($event.checked)">
          </mat-slide-toggle>
        </div>
        <button mat-raised-button color="primary" (click)="onGeneratePdfClick()"
                data-testid="standard-report-generate-pdf-button">Generate PDF</button>
        <button mat-raised-button class="margin-left-small" (click)="downloadAsCSV(standardGameReport)"
                data-testid="standard-report-generate-pdf-button">Generate CSV</button>
      </div>
    </div>

    <div class="margin-top-large">

      <!-- Game Details Section -->
      <div class="report-section">
        <h4 class="report-section-title">Game Details</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">License # <app-field-help-icon fieldKey="licenceNumber"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-license-number">{{ standardGameReport.LicenceNumber }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['licenceNumber'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Game End Date <app-field-help-icon fieldKey="endDate"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-game-end-date">{{ standardGameReport.EndDate | date: 'MMM d, y HH:mm' }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['endDate'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Draw Time <app-field-help-icon fieldKey="drawTime"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-draw-time">{{ standardGameReport.DrawTime | date: 'MMM d, y HH:mm' }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['drawTime'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Drawn # <app-field-help-icon fieldKey="winningNumber"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-winning-number">{{ standardGameReport.WinningNumber }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['winningNumber'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Drawn Person <app-field-help-icon fieldKey="winnerName"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-winner-name">{{ standardGameReport.WinnerName ? standardGameReport.WinnerName : '—' }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['winnerName'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Voided Tickets <app-field-help-icon fieldKey="voidedTickets"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-voided-tickets">{{ standardGameReport.VoidedTickets }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['voidedTickets'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Played Tickets <app-field-help-icon fieldKey="playedTickets"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-played-tickets">{{ standardGameReport.PlayedTickets }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['playedTickets'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Not Played Tickets <app-field-help-icon fieldKey="notPlayedTickets"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-unplayed-tickets">{{ standardGameReport.NotPlayedTickets }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['notPlayedTickets'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row summary-row">
            <span class="report-label bold-text">Total Tickets <app-field-help-icon fieldKey="totalTickets"></app-field-help-icon></span>
            <span class="report-value bold-text" data-testid="standard-report-total-tickets">{{ standardGameReport.VoidedTickets + standardGameReport.PlayedTickets + standardGameReport.NotPlayedTickets }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalTickets'] }}</div> }
        </div>
      </div>

      <!-- Refund Details Section -->
      <div class="report-section">
        <h4 class="report-section-title">Refund Details</h4>

        @if (standardGameReport.NumberOfRefundedPayments > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Number of Refunds Issued <app-field-help-icon fieldKey="numberOfRefundedPayments"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-refunds-issued">{{ standardGameReport.NumberOfRefundedPayments }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['numberOfRefundedPayments'] }}</div> }
          </div>

          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Number of Refunded Tickets <app-field-help-icon fieldKey="numberOfRefundedTickets"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-refunded-tickets">{{ standardGameReport.NumberOfRefundedTickets }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['numberOfRefundedTickets'] }}</div> }
          </div>

          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Total Refund Dollar Amount <app-field-help-icon fieldKey="totalRefundsDollarAmount"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-refund-total-dollar-amount">{{ standardGameReport.TotalRefundsDollarAmount | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalRefundsDollarAmount'] }}</div> }
          </div>

          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Charity Refund Fees <app-field-help-icon fieldKey="totalRefundsCharityContribution"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-charity-refund-fees">{{ standardGameReport.TotalRefundsCharityContribution | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalRefundsCharityContribution'] }}</div> }
          </div>
        } @else {
          <p class="empty-state-message">No refunds were issued during this game instance.</p>
        }
      </div>

      <!-- Dispute Details Section -->
      <div class="report-section">
        <h4 class="report-section-title">Dispute Details</h4>

        @if (standardGameReport.NumberOfDisputedPayments > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Number of Disputes <app-field-help-icon fieldKey="numberOfDisputedPayments"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-number-of-disputes">{{ standardGameReport.NumberOfDisputedPayments }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['numberOfDisputedPayments'] }}</div> }
          </div>

          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Total Dispute Dollar Amount <app-field-help-icon fieldKey="totalDisputesDollarAmount"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-dispute-total-dollar-amount">{{ standardGameReport.TotalDisputesDollarAmount | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalDisputesDollarAmount'] }}</div> }
          </div>

          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Stripe Dispute Fees <app-field-help-icon fieldKey="totalStripeDisputeFees"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-stripe-dispute-fees">{{ standardGameReport.TotalStripeDisputeFees | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalStripeDisputeFees'] }}</div> }
          </div>

          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Charity Dispute Fees <app-field-help-icon fieldKey="charityDisputeFees"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-charity-dispute-fees">{{ standardGameReport.TotalDisputeCharityContribution + standardGameReport.TotalCharityDisputeFees | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['charityDisputeFees'] }}</div> }
          </div>
        } @else {
          <p class="empty-state-message">No disputes occurred during this game instance.</p>
        }
      </div>

      <!-- Payout Details Section -->
      <div class="report-section">
        <h4 class="report-section-title">Payout Details</h4>

        @if (activeGame()?.Type === gameTypes.GoldRush) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Winning Number Played <app-field-help-icon fieldKey="winnerPlayed"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-winner-played">{{ standardGameReport.WinnerPlayed ? 'Yes' : 'No' }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['winnerPlayed'] }}</div> }
          </div>
        }

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Winner Payout <app-field-help-icon fieldKey="winnerAmount"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-winner-payout">{{ standardGameReport.WinnerAmount | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['winnerAmount'] }}</div> }
        </div>

        @if (standardGameReport.WinnerPayoutStatus) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Winner Payout Status <app-field-help-icon fieldKey="winnerPayoutStatus"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-winner-claimed-prize">{{ standardGameReport.WinnerPayoutStatus | winnerPayoutStatus }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['winnerPayoutStatus'] }}</div> }
          </div>
        }
      </div>

      <!-- Pot Total Breakdown Section -->
      <div class="report-section">
        <h4 class="report-section-title">Pot Total Breakdown</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Seed Contribution to Pot <app-field-help-icon fieldKey="seedTotal"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-seed-total">{{ standardGameReport.SeedTotal | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['seedTotal'] }}</div> }
        </div>

        @if (activeGame()?.Type === gameTypes.GoldRush) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">Rollover Contribution <app-field-help-icon fieldKey="previousPotTotal"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-previous-pot">{{ standardGameReport.PreviousPotTotal | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['previousPotTotal'] }}</div> }
          </div>
        }

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Gross Sales <app-field-help-icon fieldKey="grossSales"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-gross-sales">{{ standardGameReport.GrossSales | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['grossSales'] }}</div> }
        </div>

        <!-- Sub-items for Gross Sales breakdown -->
        <div class="report-row-wrapper">
          <div class="report-row sub-item">
            <span class="report-label">Manual Sales <app-field-help-icon fieldKey="manualSales"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-manual-sales">{{ standardGameReport.ManualSales | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['manualSales'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row sub-item">
            <span class="report-label">Autoplay Sales <app-field-help-icon fieldKey="autoplaySales"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-autoplay-sales">{{ standardGameReport.AutoplaySales | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['autoplaySales'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row sub-item">
            <span class="report-label">Dispute Fees <app-field-help-icon fieldKey="disputeFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="standard-report-total-dispute-fees">(-{{ totalDisputeFees | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['disputeFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row summary-row">
            <span class="report-label bold-text">Pot Total <app-field-help-icon fieldKey="potTotal"></app-field-help-icon></span>
            <span class="report-value bold-text" data-testid="standard-report-total-pot">{{ standardGameReport.PotTotal | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['potTotal'] }}</div> }
        </div>
      </div>

      <!-- Fee Breakdown Section -->
      <div class="report-section">
        <h4 class="report-section-title">Fee Breakdown</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Causable Fees <app-field-help-icon fieldKey="totalCausableFees"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-total-causable-fee">{{ standardGameReport.TotalCausableFees | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalCausableFees'] }}</div> }
        </div>

        @if (standardGameReport.GstOnCausableFees && standardGameReport.GstOnCausableFees > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">GST on Causable Fees <app-field-help-icon fieldKey="gstOnCausableFees"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-gst-on-causable">{{ standardGameReport.GstOnCausableFees | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['gstOnCausableFees'] }}</div> }
          </div>
        }
        @if (standardGameReport.PstOnCausableFees && standardGameReport.PstOnCausableFees > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">{{ pstLabel() }} on Causable Fees <app-field-help-icon fieldKey="pstOnCausableFees"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-pst-on-causable">{{ standardGameReport.PstOnCausableFees | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['pstOnCausableFees'] }}</div> }
          </div>
        }
        @if (standardGameReport.HstOnCausableFees && standardGameReport.HstOnCausableFees > 0 && !standardGameReport.GstOnCausableFees) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">HST on Causable Fees <app-field-help-icon fieldKey="hstOnCausableFees"></app-field-help-icon></span>
              <span class="report-value" data-testid="standard-report-hst-on-causable">{{ standardGameReport.HstOnCausableFees | formatCurrency }}</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['hstOnCausableFees'] }}</div> }
          </div>
        }

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Stripe Fees <app-field-help-icon fieldKey="totalStripeFees"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-total-stripe-fee">{{ standardGameReport.TotalStripeFees | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalStripeFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Refund Fees <app-field-help-icon fieldKey="refundFees"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-total-refund-fee">{{ standardGameReport.TotalRefundsCharityContribution | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['refundFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Stripe Dispute Fees <app-field-help-icon fieldKey="stripeDisputeFeesBreakdown"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-total-stripe-dispute-fee">{{ standardGameReport.TotalStripeDisputeFees | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['stripeDisputeFeesBreakdown'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Charity Dispute Fees <app-field-help-icon fieldKey="charityDisputeFeesBreakdown"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-charity-dispute-fee">{{ standardGameReport.TotalDisputeCharityContribution + standardGameReport.TotalCharityDisputeFees | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['charityDisputeFeesBreakdown'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Charity Host Admin Fees <app-field-help-icon fieldKey="totalAdministrationCommissionFees"></app-field-help-icon></span>
            <span class="report-value" data-testid="standard-report-host-admin-fees">{{ standardGameReport.TotalAdministrationCommissionFees | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalAdministrationCommissionFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row summary-row">
            <span class="report-label bold-text">Total Fees <app-field-help-icon fieldKey="totalFees"></app-field-help-icon></span>
            <span class="report-value bold-text" data-testid="standard-report-total-fees">{{ totalFees | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalFees'] }}</div> }
        </div>
      </div>

      <!-- Charity Revenue Breakdown Section -->
      <div class="report-section">
        <h4 class="report-section-title">Charity Revenue Breakdown</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label bold-text">Gross Sales <app-field-help-icon fieldKey="charityGrossSales"></app-field-help-icon></span>
            <span class="report-value bold-text" data-testid="standard-report-charity-breakdown-gross-sales">{{ standardGameReport.GrossSales | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['charityGrossSales'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Prize Amount <app-field-help-icon fieldKey="prizeAmount"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="standard-report-charity-prize-amount">(-{{ standardGameReport.TotalPotContribution | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['prizeAmount'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Total Fees <app-field-help-icon fieldKey="charityTotalFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="standard-report-total-fees-charity-breakdown">(-{{ totalFees | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['charityTotalFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row summary-row">
            <span class="report-label bold-text">Net Proceeds For Charity <app-field-help-icon fieldKey="netProceedsForCharity"></app-field-help-icon></span>
            <span class="report-value bold-text green-text" data-testid="standard-report-net-for-charity">{{ standardGameReport.TotalCharityContribution | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['netProceedsForCharity'] }}</div> }
        </div>

        <!-- Charity Contributions Grid -->
        <div class="charity-grid-section">
          <ag-grid-angular
            data-testid="charity-contributions-grid"
            rowClass="charity-break-down-row"
            class="ag-theme-quartz"
            (gridReady)="onGridReady($event)"
            [animateRows]="true"
            [domLayout]="'autoHeight'"
            overlayNoRowsTemplate="<span>No tickets for this game</span>"
            [rowData]="standardGameReport.CharityContributionsBreakdown"
            [columnDefs]="groupBreakdownReportDefs">
          </ag-grid-angular>
        </div>
      </div>

    </div>
  </div>
}
